import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { Calendar, ChevronLeft, ChevronRight, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_OPTIONS = ["pending", "confirmed", "completed", "cancelled"];
const statusColors = {
  confirmed: "bg-emerald-500/10 text-emerald-400",
  completed: "bg-blue-500/10 text-blue-400",
  cancelled: "bg-red-500/10 text-red-400",
  pending: "bg-amber-500/10 text-amber-400"
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const limit = 15;

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/bookings', { params: { skip, limit } });
      setBookings(res.data.bookings);
      setTotal(res.data.total);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [skip]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateStatus = async (bookingId, newStatus) => {
    setUpdating(bookingId);
    try {
      await apiClient.put(`/admin/bookings/${bookingId}/status`, { status: newStatus });
      fetchBookings();
    } catch (err) {
      // silent
    } finally {
      setUpdating(null);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-bookings">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white mb-1">Bookings Management</h1>
          <p className="text-white/40 text-sm">{total} total bookings</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : bookings.length > 0 ? (
          <>
            <div className="bg-[#161821] border border-white/5 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/30 text-xs uppercase border-b border-white/5">
                      <th className="text-left p-4">Session</th>
                      <th className="text-left p-4">Client</th>
                      <th className="text-left p-4">Date</th>
                      <th className="text-left p-4">Duration</th>
                      <th className="text-left p-4">Source</th>
                      <th className="text-left p-4">Status</th>
                      <th className="text-left p-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.booking_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`booking-row-${b.booking_id}`}>
                        <td className="p-4">
                          <p className="text-white/80 font-medium">{b.session_title}</p>
                          <p className="text-white/30 text-xs">{b.session_type}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-white/70">{b.guest_name || b.user_id || "—"}</p>
                          {b.guest_email && <p className="text-white/30 text-xs flex items-center gap-1"><Mail className="w-3 h-3" />{b.guest_email}</p>}
                        </td>
                        <td className="p-4 text-white/50">{b.scheduled_date ? new Date(b.scheduled_date).toLocaleDateString() : "—"}</td>
                        <td className="p-4 text-white/50">{b.duration_minutes} min</td>
                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${b.source === "public_booking" ? "bg-violet-500/10 text-violet-400" : "bg-white/5 text-white/40"}`}>
                            {b.source === "public_booking" ? "Website" : "In-App"}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${statusColors[b.status] || statusColors.pending}`}>
                            {b.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <select
                            value={b.status}
                            onChange={(e) => updateStatus(b.booking_id, e.target.value)}
                            disabled={updating === b.booking_id}
                            className="bg-[#0f1117] border border-white/10 text-white/70 rounded-lg px-2 py-1 text-xs"
                            data-testid={`status-select-${b.booking_id}`}
                          >
                            {STATUS_OPTIONS.map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <p className="text-white/40 text-sm">Page {currentPage} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSkip(Math.max(0, skip - limit))} disabled={skip === 0} className="border-white/10 text-white/50 hover:text-white rounded-xl">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSkip(skip + limit)} disabled={skip + limit >= total} className="border-white/10 text-white/50 hover:text-white rounded-xl">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-[#161821] border border-white/5 rounded-2xl p-12 text-center">
            <Calendar className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-white/30">No bookings yet</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
